import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { AuthService } from "./services/auth.service";
import { StorageService } from "./services/storage.service";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from 'rxjs/operators';
import { Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { LanguageService, SupportedLang } from "./shared/language.service";
import { ThemeService } from "./shared/theme.service";
import { TranslateService } from '@ngx-translate/core';
import { NotificacaoFcmService } from "./services/notificacao/notificacao-fcm.service";
import { NotificacaoBrowserService } from "./services/notificacao/notificacao-browser.service";
import { NotificacaoPedidoDTO, TipoNotificacaoPedido } from "./models/pedidos/notificacao-pedido.model";
import { CotacaoService } from "./services/cotacao/cotacao.service";
import { CotacaoDialogService } from "./services/cotacao/cotacao-dialog.service";
import { CotacaoGuardService } from "./services/cotacao/cotacao-guard.service";
import { PedidosPendentesService } from "./services/pedidos/pedidos-pendentes.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isSidebarOpen = false;
  isCardapioRoute = false;
  booting = true;
  private subs: Subscription[] = [];

  current!: SupportedLang;

  langs = [
    { code: 'pt-BR', label: 'Português (Brasil)', flag: 'assets/img/flags/br.svg' },
    { code: 'es-ES', label: 'Español',           flag: 'assets/img/flags/es.svg' },
  ];

  // Cotacao global
  dialogCotacaoVisivel = false;
  empresaId: number | null = null;
  /** Overrides do dialogo global, informados por quem o abriu (CotacaoGuardService). */
  dialogCotacaoEmpresaId: number | null = null;
  dialogCotacaoMoedaEmpresa: string | null = null;
  dialogCotacaoSubtituloChave: string | null = null;

  // Notificacao global de pedido
  dialogNotificacaoVisivel = false;
  notificacaoAtual: NotificacaoPedidoDTO | null = null;
  // Ultima notificacao chegada com aba inativa: exibe o card quando o usuario voltar
  private notificacaoPendenteAoVoltar: NotificacaoPedidoDTO | null = null;
  textsNotificacao = {
    titulo: '',
    pedido_numero: '',
    cliente: '',
    estado: '',
    ver_pedido: '',
    fechar: '',
  };

  private fcmSubscription?: Subscription;
  private langSubscription?: Subscription;
  private cotacaoDialogSub?: Subscription;

  constructor(
    private router: Router,
    private storageService: StorageService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private doc: Document,
    private authService: AuthService,
    private langService: LanguageService,
    private themeService: ThemeService,
    public translate: TranslateService,
    private fcmService: NotificacaoFcmService,
    private notificacaoBrowser: NotificacaoBrowserService,
    private cotacaoService: CotacaoService,
    private cotacaoDialogService: CotacaoDialogService,
    private cotacaoGuard: CotacaoGuardService,
    private pedidosPendentesService: PedidosPendentesService,
  ) {}


  ngOnInit() {
    this.themeService.init();
    this.current = this.langService.getCurrent();

    // Detectar rota do cardapio digital para layout isolado
    this.isCardapioRoute = this.isRotaCardapio(this.router.url);

    // Aplicar contexto de rota no carregamento inicial
    this.themeService.aplicarContextoRota(this.router.url);

    // Monitorar navegacoes futuras
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.isCardapioRoute = this.isRotaCardapio(event.urlAfterRedirects);
      this.themeService.aplicarContextoRota(event.urlAfterRedirects);
    });

    // estado inicial
    this.isAuthenticated = !!this.storageService.getItem('id');

    // espera bootstrap terminar para remover splash
    this.authService.initialized$.subscribe((init) => {
      this.booting = !init;
      if (init) this.removeBootSplash();
    });

    this.authService.isAuthenticated$.subscribe((value) => {
      this.isAuthenticated = value;
      if (value) {
        // Usuario autenticado: inicializar FCM (permissao push + registro de token)
        this.inicializarFcm();
        // Verificar cotacao do dia
        this.verificarCotacaoGlobal();
      } else {
        this.isSidebarOpen = false;
        // Usuario deslogado: desativar FCM e limpar subscricao
        this.fcmSubscription?.unsubscribe();
        this.fcmSubscription = undefined;
        this.fcmService.desativar();
      }
    });

    // Escutar solicitacoes de abertura do dialogo de cotacao (ex: F8 na tela de vendas)
    this.cotacaoDialogSub = this.cotacaoDialogService.abrirDialog$.subscribe((opts) => {
      this.dialogCotacaoEmpresaId = opts?.empresaId ?? null;
      this.dialogCotacaoMoedaEmpresa = opts?.moedaEmpresa ?? null;
      this.dialogCotacaoSubtituloChave = opts?.subtituloChave ?? null;
      this.dialogCotacaoVisivel = true;
    });

    // Carregar traducoes do dialog de notificacao
    this.carregarTextsNotificacao();
    this.langSubscription = this.translate.onLangChange.subscribe(() => {
      this.carregarTextsNotificacao();
    });

    // Exibir card da ultima notificacao recebida com aba inativa, ao voltar ao foco
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('visibilitychange', this.onAbaVisivelHandler);
    }
  }

  private inicializarFcm(): void {
    // Guard: nao duplicar subscricao ao observable
    if (!this.fcmSubscription) {
      this.fcmSubscription = this.fcmService.notificacoes$.subscribe(
        (notificacao) => {
          this.onNotificacaoGlobalRecebida(notificacao);
        }
      );
    }

    // Inicializar FCM (permissao, token, listener)
    this.fcmService.inicializar();
  }

  private onNotificacaoGlobalRecebida(notificacao: NotificacaoPedidoDTO): void {
    if (notificacao.tipo !== TipoNotificacaoPedido.NOVO_PEDIDO) {
      return;
    }

    if (this.notificacaoBrowser.abaVisivel) {
      // Aba ativa: exibir dialog global e tocar som
      this.notificacaoAtual = notificacao;
      this.dialogNotificacaoVisivel = true;
      this.notificacaoBrowser.tocarSom();
    } else {
      // Aba inativa: titulo piscante + som + browser notification
      const titulo = this.translate.instant('notificacao_pedido.texts.browser_notificacao_titulo');
      const corpo = this.translate.instant('notificacao_pedido.texts.browser_notificacao_corpo', {
        codigo: notificacao.codigoDePedido,
        cliente: notificacao.nomeCliente,
      });
      const tituloAba = this.translate.instant('notificacao_pedido.texts.novo_pedido_recebido');
      this.notificacaoBrowser.notificarAbaInativa(titulo, corpo, tituloAba);
      // Guarda apenas a ultima para exibir o card quando a aba voltar ao foco
      this.notificacaoPendenteAoVoltar = notificacao;
    }
  }

  // Handler de visibilitychange: ao voltar para a aba, exibe o card da ultima
  // notificacao recebida enquanto estava inativa (se houver).
  private onAbaVisivelHandler = (): void => {
    if (!isPlatformBrowser(this.platformId)) return;
    if (document.visibilityState !== 'visible') return;
    if (!this.notificacaoPendenteAoVoltar) return;
    // Nao sobrescrever um card ja aberto
    if (this.dialogNotificacaoVisivel) return;

    const pendente = this.notificacaoPendenteAoVoltar;
    this.notificacaoPendenteAoVoltar = null;
    this.notificacaoAtual = pendente;
    this.dialogNotificacaoVisivel = true;
  };

  private carregarTextsNotificacao(): void {
    this.textsNotificacao = {
      titulo: this.translate.instant('notificacao_pedido.texts.novo_pedido_recebido'),
      pedido_numero: this.notificacaoAtual
        ? this.translate.instant('notificacao_pedido.texts.pedido_numero', { codigo: this.notificacaoAtual.codigoDePedido })
        : '',
      cliente: this.translate.instant('notificacao_pedido.texts.cliente'),
      estado: this.translate.instant('notificacao_pedido.texts.estado'),
      ver_pedido: this.translate.instant('notificacao_pedido.botoes.ver_pedido'),
      fechar: this.translate.instant('notificacao_pedido.botoes.fechar'),
    };
  }

  onVerPedidoNotificacaoGlobal(pedidoId: number): void {
    this.dialogNotificacaoVisivel = false;
    this.notificacaoAtual = null;
    const urlAtual = this.router.url;
    if (urlAtual.startsWith('/pedidos')) {
      // Ja esta na pagina de pedidos: comunicar diretamente via servico
      // (same-URL navigation e ignorada pelo router e nao emite NavigationEnd)
      this.pedidosPendentesService.solicitarAberturaPedido(pedidoId);
    } else {
      // Navegar para /pedidos com state para que o componente abra o detalhe ao inicializar
      this.router.navigate(['/pedidos'], { state: { abrirPedidoId: pedidoId } });
    }
  }

  private verificarCotacaoGlobal(): void {
    this.empresaId = Number(this.storageService.getItem('id')) || null;
    // Delay para garantir que o token JWT ja esta disponivel
    setTimeout(() => {
      // Mesma regra das operacoes (GET /cotacao/cotacaoAtual): sem cotacao de hoje,
      // abre o dialogo. Silencioso: cancelar aqui nao precisa de aviso.
      this.cotacaoGuard.garantirCotacao({ silencioso: true });
    }, 1500);
  }

  onCotacaoSalva(): void {
    this.dialogCotacaoVisivel = false;
    this.dialogCotacaoEmpresaId = null;
    this.dialogCotacaoMoedaEmpresa = null;
    this.dialogCotacaoSubtituloChave = null;
  }

  onSidebarStateChange(isOpen: boolean) {
    this.isSidebarOpen = isOpen;
  }

  private removeBootSplash() {
    if (!isPlatformBrowser(this.platformId)) return;
    const el = this.doc?.getElementById('boot-splash');
    if (el?.parentElement) el.parentElement.removeChild(el);
  }

  /** Retorna true para rotas que devem renderizar sem o layout do ERP */
  private isRotaCardapio(url: string): boolean {
    return url.startsWith('/cardapios') || url.startsWith('/menu/');
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
    this.fcmSubscription?.unsubscribe();
    this.langSubscription?.unsubscribe();
    this.cotacaoDialogSub?.unsubscribe();
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('visibilitychange', this.onAbaVisivelHandler);
    }
  }
}
